import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';

const LOGO_URL = "/Logo-for-web.png";

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/why-choose-us', label: 'Why Choose Us' },
    { path: '/channels', label: 'Channels' },
    { path: '/demo', label: 'Demo' },
    { path: '/isp-solutions', label: 'ISP Solutions' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="bg-[#0A0A0A] text-white py-2">
        <div className="container-custom flex flex-wrap justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:9019206699" className="flex items-center gap-1 hover:text-red-400 transition-colors" data-testid="header-phone">
              <Phone size={14} strokeWidth={2} />
              <span>9019206699</span>
            </a>
            <a href="mailto:info@adigital.tv" className="hidden sm:flex items-center gap-1 hover:text-red-400 transition-colors" data-testid="header-email">
              <Mail size={14} strokeWidth={2} />
              <span>info@adigital.tv</span>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-1 text-gray-400">
            <MapPin size={14} strokeWidth={2} />
            <span>Bangalore, India</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" data-testid="main-navigation">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="A Digital Logo" 
                className="h-10 md:h-12 w-auto object-contain"
              />
              <span className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
                A<span className="text-[#D92323]">Digital</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-semibold uppercase tracking-wide transition-colors ${
                    isActive(link.path) 
                      ? 'text-[#D92323] active' 
                      : 'text-gray-700 hover:text-[#D92323]'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a 
                href="tel:9019206699" 
                className="btn-primary text-sm"
                data-testid="cta-call-now"
              >
                Call Now
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-[#D92323] transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 animate-fadeIn">
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`py-3 px-4 rounded-lg font-semibold transition-colors ${
                      isActive(link.path)
                        ? 'bg-red-50 text-[#D92323]'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <a 
                  href="tel:9019206699" 
                  className="mt-2 btn-primary text-center"
                  data-testid="mobile-cta-call"
                >
                  Call Now
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
        <div className="container-custom py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img 
                  src={LOGO_URL} 
                  alt="A Digital Logo" 
                  className="h-10 w-auto object-contain"
                />
                <span className="text-xl font-bold tracking-tight">
                  A<span className="text-[#D92323]">Digital</span>
                </span>
              </Link>
              <p className="text-gray-400 leading-relaxed mb-4 max-w-md">
                Leading provider of FTTH and IPTV solutions in Bangalore. Crystal clear 4K signals through pure glass fiber technology.
              </p>
              <p className="text-sm text-gray-500">
                Assistive Netspeed Technologies Private Limited
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-gray-400 hover:text-[#D92323] transition-colors"
                      data-testid={`footer-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-bold text-lg mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li>
                  <a href="tel:9019206699" className="flex items-start gap-2 text-gray-400 hover:text-[#D92323] transition-colors">
                    <Phone size={18} className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <span className="block">9019206699</span>
                      <span className="text-sm text-gray-500">Regional Bangalore Line</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a href="mailto:support@adigital.tv" className="flex items-start gap-2 text-gray-400 hover:text-[#D92323] transition-colors">
                    <Mail size={18} className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <span>support@adigital.tv</span>
                  </a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <MapPin size={18} className="mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <div>
                    <span className="block">#330, Whitecity Layout</span>
                    <span className="block">Bangalore-49</span>
                    <span className="text-sm text-gray-500">Opp. Blossom International School</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} A Digital. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-[#D92323] transition-colors">Privacy Policy</Link>
              <span className="text-gray-700">|</span>
              <span className="text-gray-500">TRAI Compliant Channel Packages</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
